import axios, {HttpStatusCode} from 'axios';
import {saveAs} from 'file-saver';
import {format} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {Buffer} from 'buffer';
import iconv from 'iconv-lite';

import {CATEGORY_TYPE, COMPANY_TYPE, IDO_TYPE, DNT_ID_TYPE, NW_OFFICE_CODE, SEARCHRESULT_MAX} from './commonConst.js';
import conf from '../config/config.js';
import {downloadRequest} from './backend.js';
import {errorResult} from './inputCheck.js';

/* eslint no-irregular-whitespace: ["error", {"skipRegExps": true}] */

const timeZone = 'Asia/Tokyo';
const DATE_DEFAULT = 'yyyy/MM/dd';
export const ISO_LOCAL_DATE = 'yyyy-MM-dd';
export const ISO_LOCAL_DATE_TIME = 'yyyy-MM-dd HH:mm:ss.SSS';

/**
 * APIエラーコード。
 * KMS-APIのエラーコード定義と同時に更新する必要有。
 */
export const API_CODE = {
  /** 成功 */
  SUCCESS: '00000',

  /** 登録エラー */
  ERROR_REGIST: '00001',

  /** 取得エラー */
  ERROR_GET: '00002',

  /** ID指定取得エラー */
  ERROR_RETRIEVE: '00003',

  /** 更新エラー */
  ERROR_UPDATE: '00004',

  /** 削除エラー */
  ERROR_DELETE: '00005',

  /** ファイルアップデートエラー */
  ERROR_FILE_UPDATE: '00006',

  /** ウィルスチェックエラー */
  ERROR_VIRUS: '10001',

  /** 権限不足エラー */
  ERROR_PERMISSION_DENIED: '20000',

  /** Cognitoユーザーデータ取得失敗 */
  ERROR_GET_USER_DATA: '21200',

  /** バリデーションエラー */
  ERROR_VALIDATION: '90000',

  /** 最終更新日時不一致 */
  ERROR_LAST_MODIFIED_DATE: '90001',

  /** システムエラー */
  SYS_ERROR: '99999',
};

const harfKanaMap = {
  'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
  'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
  'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
  'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
  'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
  'ｳﾞ': 'ヴ', 'ﾜﾞ': 'ヷ', 'ｦﾞ': 'ヺ',
  'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
  'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
  'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
  'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
  'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
  'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
  'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
  'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
  'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
  'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
  'ｧ': 'ァ', 'ｨ': 'ィ', 'ｩ': 'ゥ', 'ｪ': 'ェ', 'ｫ': 'ォ',
  'ｯ': 'ッ', 'ｬ': 'ャ', 'ｭ': 'ュ', 'ｮ': 'ョ',
  '｡': '。', '､': '、', 'ｰ': 'ー', '｢': '「', '｣': '」', '･': '・',
};

// 全角→半角変換
export const full2Harf = (full) => {
  if (!full) return '';

  // アルファベット 数字 記号
  let harf = full.replace(/[Ａ-Ｚａ-ｚ０-９！-～]/g, function(s) {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
  });

  // 全角カナ→半角カナ
  const reg = new RegExp('(' + Object.values(harfKanaMap).join('|') + ')', 'g');
  const fullKanaMap = {};
  for (const item in harfKanaMap) {
    if (harfKanaMap.hasOwnProperty(item)) {
      fullKanaMap[harfKanaMap[item]] = item;
    }
  }
  harf = harf.replace(reg, function(match) {
    return fullKanaMap[match];
  })

      .replace('゛', /ﾞ/g)
      .replace('゜', /ﾟ/g)
      .replace(/　/g, ' ');

  return harf;
};

// 全角かな→半角ｶﾅ変換
export const full2Harf2 = (full) => {
  if (!full) return '';

  // 全角かな→全角カナ
  const kana = full.replace(/[\u3041-\u3096]/g, function(match) {
    const chr = match.charCodeAt(0) + 0x60;
    return String.fromCharCode(chr);
  });

  // 全角→半角変換
  const harf = full2Harf(kana);

  return harf;
};

// 半角→全角変換
export const harf2Full = (harf) => {
  if (!harf) return '';

  // アルファベット 数字 記号
  let full = harf.replace(/[A-Za-z0-9!-~]/g, function(s) {
    return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
  });

  // 半角カナ→全角カナ
  const reg = new RegExp('(' + Object.keys(harfKanaMap).join('|') + ')', 'g');
  full = full.replace(reg, function(match) {
    return harfKanaMap[match];
  })
      .replace(/ﾞ/g, '゛')
      .replace(/ﾟ/g, '゜')
      .replace(/ /g, '　');

  return full;
};

// base64からBlob変換
export const base64toBlob = (base64, mimeCtype) => {
  const bin = window.atob(base64.replace(/^.*,/, ''));
  const buffer = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i);
  }
  // Blobを作成
  let blob;
  try {
    blob = new window.Blob([buffer.buffer], {
      type: mimeCtype,
    });
  } catch (e) {
    return false;
  }
  return blob;
};

// base64からBlob変換（CSV用 UTF-8 → SJIS変換）
export const base64toBlobCsv = (base64, mimeCtype) => {
  try {
    // base64デコード
    const buffer = Buffer.from(base64, 'base64').toString();

    // UTF-8からSJIS変換
    const csvData = utf8toSjis(buffer);

    // Blobを作成
    return new window.Blob([csvData], {
      type: mimeCtype,
    });
  } catch (e) {
    console.error(e);
    return false;
  }
};

// 文字列からBlob変換
export const string64toBlob = (str, mimeCtype) => {
  try {
    return new window.Blob([str], {
      type: mimeCtype,
    });
  } catch (e) {
    console.error(e);
    return false;
  }
};

// SJISからUTF-8変換
export const sjisToUtf8 = (str) => {
  try {
    // 文字コード変換(SJIS → UTF-8)
    const data = iconv.decode(str, 'shift-jis');
    return data.replace(/\r\n/g, '\n');
  } catch (e) {
    console.error(e);
    return false;
  }
};

// UTF-8からSJIS変換
export const utf8toSjis = (str) => {
  try {
    // 改行コード変換(\n → \r\n)
    const dataCrlf = str.replace(/\n/g, '\r\n');
    // 文字コード変換(UTF-8 → SJIS)
    const dataSjis = iconv.encode(dataCrlf, 'Shift_JIS');
    return dataSjis;
  } catch (e) {
    console.error(e);
    return false;
  }
};

// ブラウザ判定
export const browser = () => {
  const ua = window.navigator.userAgent.toLowerCase();

  // Mac
  if (ua.indexOf('mac os x') !== -1) {
    // Chrome
    if (ua.indexOf('chrome' && ua.indexOf('edge') == -1) !== -1) {
      return 'MAC_CHROME';
    }
    // Safari
    if (ua.indexOf('safari') !== -1) {
      return 'MAC_SAFARI';
    }
  }

  // iPhone
  if (ua.indexOf('iphone') !== -1) {
    // Chrome
    if (ua.indexOf('chrome') !== -1 && ua.indexOf('edge') == -1) {
      return 'IOS_CHROME';
    }
    // Safari
    if (ua.indexOf('safari') !== -1) {
      return 'IOS_SAFARI';
    }
  }

  return 'OTHER';
};

/**
 * スマートフォン判定
 * @return {boolean} iOS/Androidの場合true、それ以外の場合false
 */
export const isSmartPhone = () => {
  return navigator.userAgent.match(/iPhone|iPad|Android/);
};

/**
 * NULLチェック
 * @param {object} obj
 * @return {boolean} true:null
 */
export const isNull = (obj) => {
  return obj == undefined || obj == null;
};

/**
 * オブジェクトの空判定
 * @param {object} obj オブジェクト
 * @return {boolean} true:空, false:空以外
 */
export const isEmptyObject = (obj) => {
  return (isNull(obj) || Object.keys(obj).length === 0);
};

/**
 * 帳票(EXCEL)の作成
 *
 * @param {object} userInfo
 * @param {string} tyohyoName
 * @param {object} param
 * @param {string} fileName
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
export async function createTyohyoExcel(userInfo, tyohyoName, param, fileName) {
  return await createTyohyo(userInfo, tyohyoName, fileName,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', param);
}

/**
 * 帳票(PDF)の作成
 *
 * @param {object} props
 * @param {string} id
 * @param {object} param
 * @param {string} fileName
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
export async function createTyohyoPdf(props, id, param = {}, fileName = '') {
  return await createTyohyo(props, id, param, fileName);
}

/**
 * 帳票(CSV)の作成
 *
 * @param {object} props
 * @param {string} id
 * @param {object} param
 * @param {string} fileName
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
export async function createTyohyoCsv(props, id, param = {}, fileName = '') {
  return await createTyohyo(props, id, param, fileName);
}

/**
 * 帳票(CSV)の作成（salesforceの検索上限2000件を超えてデータを取得）
 *
 * @param {string} tyohyoName
 * @param {object} param
 * @param {string} fileName
 * @param {object} subParam
 * @param {number} LimitSize
 * @return {number} totalSize 取得した件数（エラーの場合は -1を返却）
 */
export async function createTyohyoCsvLimitOver(
    tyohyoName, param, fileName, subParam, LimitSize) {
  try {
    const body = {};
    if (param) {
      body['conditions'] = param;
    }
    if (subParam) {
      body['subquery'] = subParam;
    }
    const ret = await axios.post(conf.API_URL + 'Tyohyo/' + tyohyoName, body, {});
    const totalSize = ret.data.body.totalSize;

    if (ret.data.body.errorCode === '00000') {
      if (totalSize && totalSize > LimitSize) {
        return totalSize;
      } else if (totalSize == 0) {
        return totalSize;
      }
      const blob = base64toBlobCsv(ret.data.body.data.contentDocumentBody, 'text/csv');
      if (blob) {
        saveAs(blob, fileName);
        return totalSize;
      }
      return -1;
    }
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * 帳票の作成 共通部分
 *
 * @param {object} props
 * @param {string} id
 * @param {object} body
 * @param {string} fileName
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
async function createTyohyo(props, id, body, fileName) {
  try {
    const response = await downloadRequest('v1.0/' + id.toLowerCase(), body);

    if (response.status !== HttpStatusCode.Ok) {
      if (response.data && response.data.message !== null) {
        props.doShowMessage({
          message: {id: response.data.message.id,
            text: response.data.message.text},
        });
      }
    } else {
      const outputName = fileName || decodeURI(response.headers['content-disposition'].split('filename*=')[1].split(';')[0]).replace(/"/g, '');
      saveAs(response.data, outputName);
    }
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * 添付ファイルダウンロード
 *
 * @param {object} userInfo ユーザ情報(必須)
 * @param {string} filePath ファイルパス(必須)
 * @param {string} type     ファイルタイプ(必須)
 * @param {string} fileName 出力ファイル名(必須)
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
export async function downloadAttachedFile(userInfo, filePath, type, fileName) {
  const wkType = 'application/' + type;
  return downloadFile(userInfo, filePath, wkType, fileName);
}

/**
 * ファイルダウンロード
 *
 * @param {object} userInfo ユーザ情報(必須)
 * @param {string} filePath ファイルパス(必須)
 * @param {string} type     ファイルタイプ(必須)
 * @param {string} fileName 出力ファイル名(必須)
 * @return {Promise<boolean>} 成功時はtrue、失敗時はfalse
 */
export async function downloadFile(userInfo, filePath, type, fileName) {
  // すでに取得済の場合は処理終了
  if (!(userInfo && filePath && type && fileName)) {
    console.log('引数が未設定のため、終了');
    return;
  }

  const body = {};
  body['fileName'] = filePath;
  body['downloadFileName'] = fileName;

  try {
    const ret = await axios.post(conf.API_URL + 'AttachedFile/getFile', body, {
      headers: {
        Authorization: userInfo.idToken,
      },
    });

    if (ret.data.body.errorCode === '00000') {
      const anchor = document.createElement('a');
      anchor.download = fileName;
      anchor.href = ret.data.body.data;
      anchor.click();
      return true;
    }

    return false;
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * ファイルダウンロードコンテンツ取得
 *
 * @param {object} userInfo ユーザ情報(必須)
 * @param {string} filePath ファイルパス(必須)
 * @param {string} type     ファイルタイプ(必須)
 * @param {string} fileName 出力ファイル名(必須)
 * @return {Blob} 失敗時はnull
 */
export async function downloadFileContent(userInfo, filePath, type) {
  // すでに取得済の場合は処理終了
  if (!(userInfo && filePath && type)) {
    console.log('引数が未設定のため、終了');
    return null;
  }

  const body = {};
  body['fileName'] = filePath;

  try {
    const ret = await axios.post(conf.API_URL + 'AttachedFile/getFile', body, {
      headers: {
        Authorization: userInfo.idToken,
      },
    });

    if (ret.data.body.errorCode === '00000') {
      return ret.data.body.data;
    }

    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * 日付型（UTC）のフォーマット変換
 *
 * @param {string} date 文字列
 * @param {string} [changeFormat] 出力フォーマット
 * @return {string}} 変換した日付
 */
export function formatUtcDate(date, changeFormat = DATE_DEFAULT) {
  if (!date) {
    return '';
  }
  const zonedDate = utcToZonedTime(new Date(date), timeZone);
  return changeDateFormatter(zonedDate, changeFormat);
}

/**
 * 日付型のフォーマット変換
 *  @param {string} date
 * @param {string} changeFormat
 * @return {string} 変換後の日付データ
 */
export function formatStringDate(date, changeFormat = DATE_DEFAULT) {
  if (!date) {
    return '';
  }
  return changeDateFormatter(new Date(date), changeFormat);
}

/**
 * 日付型のフォーマット変換
 *  @param {number|Object} date
 * @param {string} changeFormat
 * @return {string} 変換後の日付データ
 */
export function formatDate(date, changeFormat = DATE_DEFAULT) {
  return changeDateFormatter(date, changeFormat);
}

/**
 * 日付型のフォーマット変換
 * @param {number|Object} date
 * @param {string} changeFormat
 * @return {string} 変換後の日付データ
 */
export function changeDateFormatter(date, changeFormat) {
  return format(date, changeFormat);
}

/**
 * 文字列から費用のフォーマット変換
 * @param {string} value
 * @return {string} 変換後のデータ
 */
export function formatStringToPrice(value) {
  if (!value) {
    return value;
  }
  let formattedValue = value.toLocaleString('ja-JP');
  formattedValue += '円';
  return formattedValue;
}

/**
 * 事業者コードがエネコムかどうか判定する
 * @param {string} kyogaZgsyaCode チェック対象
 * @param {array} items 汎用マスタのエネコム共架事業者コード
 * @return {boolean} true:エネコム, false:エネコム以外
 */
export function isEnecom(kyogaZgsyaCode, items) {
  let ret = false;

  if (!kyogaZgsyaCode) {
    return ret;
  }
  if (!items) {
    return ret;
  }

  for (const item in items) {
    if (items[item].ValueString1__c == kyogaZgsyaCode) {
      ret = true;
      break;
    }
  }
  return ret;
}

/**
 * 事業者コードが中電NW設計事業者かどうか判定する。
 * @param {object} userInfo ユーザ情報
 * @param {object} generalPurposeMap 汎用マスタ
 * @return {boolean} true:中電NW設計事業者, false:中電NW設計事業者以外
 */
export function isNWDesignZgsya(userInfo, generalPurposeMap) {
  // const account = userInfo.Account;
  // if (!account) {
  //   // SFユーザの場合はfalse
  //   return false;
  // }

  // const kyogaZgsyaCode = userInfo.Account.KyogaZgsyaCode__c;
  // const items = generalPurposeMap.NWDesignZgsyaCode;

  // // 実装はisEnecomと同じ
  // return isEnecom(kyogaZgsyaCode, items);
  return true;
}

/**
 * 汎用マスタの全データから汎用マスタの外部IDに紐づくコード値を取得する
 * 外部IDは「区分種別＋コード」になっているので、
 * 汎用マスタにデータがなければ外部IDからコード値を切り出して返す
 * @param {object} allGeneralPurposeMap 汎用マスタの全データ
 * @param {string} externalId 汎用マスタの外部ID
 * @param {number} digit コード値の桁数
 * @return {string} コード値
 */
export function getCodeFromGeneralPurposeMap(
    allGeneralPurposeMap, externalId, digit) {
  if (!externalId || !digit) {
    return '';
  }
  const key = externalId.substring(0, externalId.length - digit);
  const code = externalId.substring(
      externalId.length - digit, externalId.length);
  if (!allGeneralPurposeMap || !allGeneralPurposeMap[key]) {
    return code;
  }
  const result =
    allGeneralPurposeMap[key]
        .filter((data) => data.ExternalId__c == externalId);
  return result.length > 0 ? result[0].Code__c : code;
}

/**
 * 汎用マスタを用いたコード→名称変換用MAP生成
 *
 * @param {object} genericMap 全汎用マスタ
 * @param {string[]} categories 使用する区分一覧
 * @return {object} 変換用MAP
 */
export function createGenericNameMap(genericMap, categories) {
  const genericNameMap = {};

  for (const category of categories) {
    const itemMap = {};
    genericNameMap[category] = itemMap;

    for (const item of genericMap[category]) {
      itemMap[item.cd] = item.cdNameAbbr;
    }
  }

  return genericNameMap;
}

/**
 * 数値を3桁カンマ区切りにする
 * @param {number} num 数値
 * @return {string} 3桁カンマ区切りにした文字列
 */
export function convertCommaSeparated(num) {
  return num ? num.toLocaleString('ja-JP') : num;
}

/**
 * 汎用マスタからコンボボックス表示用リストを取得する
 * @param {object} generalPurposeMap 汎用マスタデータ
 * @param {string} kbn 汎用マスタの区分種別
 * @param {bool} emptyHeader true:表示リストの先頭行に空白あり, false:先頭行に空白なし
 * @param {object} options 除外条件 {key: [value, ...]}
 * @return {array} コンボボックス表示用リスト [{label: 表示名称, value:コード値}]
 */
export function getComboList(
    generalPurposeMap, kbn, emptyHeader=true, options={}) {
  let resultList = [{label: '', value: ''}];
  if (!generalPurposeMap ||
    !Object.hasOwnProperty.call(generalPurposeMap, kbn)) {
    return resultList;
  }

  if (!emptyHeader) {
    resultList = [];
  }

  for (const data of generalPurposeMap[kbn]) {
    if (Object.keys(options).length > 0) {
      let skip = false;
      for (const key of Object.keys(options)) {
        if (!options[key]) {
          continue;
        }
        let _array = [];
        if (Array.isArray(options[key])) {
          _array = options[key];
        } else {
          _array.push(options[key]);
        }
        skip = Object.hasOwnProperty.call(data, key) &&
          (_array.includes(data[key]));
      }
      if (skip) {
        continue;
      }
    }
    resultList.push({label: data.cdNameAbbr, value: data.cd});
  }

  return resultList;
}

/**
 * コード値をキーにしてコンボボックス表示用リストから表示名称を取得する
 * @param {object} genericMst 汎用マスタデータ
 * @param {string} kbn 汎用マスタの区分種別
 * @param {string} code コード値
 * @return {array} コード値に対応する表示名称
 */
export function getComboName(genericMst, kbn, code) {
  const list = getComboList(genericMst, kbn);
  const obj = list ? list.filter(
      (data)=>data.value == code) : [];
  return obj && obj.length > 0 ? obj[0].label : '';
}

/**
 * 画面コンボボックス表示用リスト作成
 * @param {array} itemsList
 * @param {string} label
 * @param {array} comboList
 * @param {bool} emptyHeader true:表示リストの先頭行に空白あり, false:先頭行に空白なし
 */
export function setComboList(itemsList, label, comboList, emptyHeader=false) {
  let resultList = [];
  if (emptyHeader) {
    resultList = [{cd: '', cdNameAbbr: '　'}];
  }
  resultList.push(...comboList);
  const index = itemsList.findIndex((element) => {
    return element.label === label;
  });
  if (index !== -1) {
    itemsList[index].selections = resultList;
  }
};

/**
 * 事業場の選択リストを取得する
 * @param {array} zigyojyoList 電柱マスタの事業場リスト
 * @param {string} labelKey 選択リストの表示名を設定する
 * @param {string} valueKey 選択リストの値を設定する
 * @param {string} emptyHeader true:表示リストの先頭行に空白あり, false:先頭行に空白なし
 * @return {array} 事業場の選択リスト
 */
export function getZigyojoSelectList(zigyojyoList, labelKey='cdName', valueKey='cd', emptyHeader=true) {
  let list = [{[labelKey]: '', [valueKey]: ''}];
  if (zigyojyoList === undefined || zigyojyoList === null) {
    return list;
  }

  if (!emptyHeader) {
    list = [];
  }

  for (const data of zigyojyoList) {
    list.push({[labelKey]: data.zigyojoName, [valueKey]: data.zigyojoCd});
  }

  return list;
}

/**
 * KMS-apiの戻り値からエラーコードを取得する
 * @param {object} axiosResponse
 * @return {*} API_CODE
 */
export function getErrorCode(axiosResponse) {
  return axiosResponse ? axiosResponse.data ? axiosResponse.data.body ?
  axiosResponse.data.body.errorCode : undefined : undefined : undefined;
}

/**
   * 条件に一致するデータのindexを返す
   * @param {array} list
   * @param {func} callback
   * @return {number} インデックス
   */
export function findIndex(list, callback) {
  if (!list || !callback) {
    return -1;
  }
  return list.findIndex(callback);
};

/**
 * 汎用マスタから区分に対応するコード一覧を取得する
 * @param {object} genericMap
 * @param {string} categoryType
 * @return {array} コード一覧
 */
export function searchMap(genericMap, categoryType) {
  return genericMap && (categoryType in genericMap) ?
    genericMap[categoryType] : [];
}

/**
 * 汎用マスタから統合後NW事業所を取得する
 * @param {object} genericMap
 * @param {string} categoryType
 * @return {array} コード一覧
 */
export function searchNewNwOffice(genericMap, categoryType) {
  const nwGenericMap = genericMap && (categoryType in genericMap) ?
    genericMap[categoryType] : [];
  if (nwGenericMap.length===0) {
    return nwGenericMap;
  }

  let filteredItems = nwGenericMap.filter((item) =>
    item.valueString3 === '1');
  let valueStr4List=[];
  for (const filteredItem of filteredItems) {
    valueStr4List.push(filteredItem.valueString4);
  }
  let uniqueArr = [...new Set(valueStr4List)];
  const resultMap=filteredItems.filter((item) =>
    uniqueArr.includes(item.cd));
  return resultMap;
}

/**
 * 汎用マスタからNW事業所(統合後のNW事業所)のコード略称を取得する
 * @param {object} genericMap
 * @param {array} nwOfficeCodes NW事業所(統合後のNW事業所)
 * @return {array} NW事業所のコード略称
 */
export function getNewNwOfficeMap(genericMap, nwOfficeCodes) {
  const categoryType = CATEGORY_TYPE.NW_OFFICE_CODE;
  let nwOfficeMap = genericMap && (categoryType in genericMap) ?
    genericMap[categoryType] : [];
  if (nwOfficeMap.length === 0) {
    return nwOfficeMap;
  }

  if (!nwOfficeCodes || nwOfficeCodes.length === 0 ||
      nwOfficeCodes.includes(NW_OFFICE_CODE.CHUDEN_NW)) {
    nwOfficeCodes = [...new Set(
        nwOfficeMap.filter((item) =>item.valueString3 === '1')
            .map((obj) => obj.valueString4))];
  }

  let newNwOfficeMap = [...new Set(nwOfficeMap
      .filter((item) => nwOfficeCodes.includes(item.cd))
      .map((obj) => ({
        cd: obj.cd,
        cdNameAbbr: obj.cdNameAbbr,
      })))];

  return newNwOfficeMap;
}

/**
 * 汎用マスタから旧NW事業所(統合前のNW事業所)のコード略称を取得する
 * @param {object} genericMap
 * @param {array} nwOfficeCodes NW事業所(統合後のNW事業所)
 * @return {array} 旧NW事業所のコード略称
 */
export function getOldNwOfficeMap(genericMap, nwOfficeCodes) {
  const categoryType = CATEGORY_TYPE.NW_OFFICE_CODE;
  let nwOfficeMap = genericMap && (categoryType in genericMap) ?
    genericMap[categoryType] : [];
  if (nwOfficeMap.length === 0) {
    return nwOfficeMap;
  }

  if (!nwOfficeCodes || nwOfficeCodes.length === 0 ||
      nwOfficeCodes.includes(NW_OFFICE_CODE.CHUDEN_NW)) {
    nwOfficeCodes = [...new Set(
        nwOfficeMap.filter((item) =>item.valueString3 === '1')
            .map((obj) => obj.valueString4))];
  }

  let oldNwOfficeMap = [...new Set(nwOfficeMap
      .filter((item) => nwOfficeCodes.includes(item.valueString4))
      .map((obj) => ({
        cd: obj.cd,
        cdNameAbbr: obj.cdNameAbbr,
      })))];

  return oldNwOfficeMap;
}

/**
 * 検索時チェック
 * @param {object} dataList
 * @return {array} エラーメッセージ
 */
export function searchCheck(dataList) {
  let _warning = [];
  let resultList = [];
  // 検索結果最大表示件数超過 エラー表示
  if (dataList.length > SEARCHRESULT_MAX) {
    resultList.push(errorResult('TKSCS00061E', [SEARCHRESULT_MAX]));
    // 検索結果最大表示件数以降の項目を除去
    dataList.splice(SEARCHRESULT_MAX);
  }
  for (const result of resultList) {
    if (result.error) {
      _warning.push(result.message);
    }
  }
  return _warning;
};

/**
 * @typedef Filter コンボ絞り込みオプション
 * @property {array} setubiType 架設設備種別
 * @property {string} companyType 会社（NTT,NTT以外)
 */
/**
 * コンボデータ取得パラメータ
 * @property {string} キー 画面項目のバインディング、またはユーザが定義したキー
 * @property {string} categoryType 汎用マスタの区分
 * @property {Filter} filters コンボ絞り込みオプション
 */
const dataMapping = {

};

/**
 * 汎用マスタからコード一覧（絞り込み）を取得する
 * @param {object} genericMap 汎用マスタ
 * @param {string} key バインドキー
 * @return {array} コード一覧
 */
export function getFilteringMap(genericMap, key) {
  const mapping = (
    !dataMapping[key] ? {categoryType: key} :
    dataMapping[key]);
  const category = mapping.categoryType;
  const filters = mapping.filters;
  let items = genericMap[category];
  if (!items) {
    return [];
  }
  if (key == 'idoType_kisetu') {
    items = genericMap[category].filter((data) => data.cd != IDO_TYPE.SHINSETU);
  } else if (key == 'idoType_sinsetu') {
    items = genericMap[category].filter((data) => data.cd == IDO_TYPE.SHINSETU);
  } else if (key == 'saisekkinHaidenSetubi') {
    items = genericMap[category].filter((data) =>
      [COMPANY_TYPE.ALL, filters.companyType].includes(data.valueString1));
  } else if (filters) {
    // filtersの定義内容 {汎用マスタのカラム: [対象値の配列]}
    // filtersに定義した内容のいずれかの条件に一致した場合、選択値に含める
    items = items.filter((data) => {
      let result = false;
      for (const filterKey of Object.keys(filters)) {
        if (filters[filterKey].includes(data[filterKey])) {
          result = true;
          break;
        }
      }
      return result;
    });
  }
  return items;
}

/**
 * メッセージに埋め込むラベル名を作成
 * @param {string} groupName グループ名
 * @param {string} headerName ヘッダー名
 * @return {string} メッセージに埋め込むラベル名
 */
export function createMessageLabel(groupName, headerName) {
  return groupName ? groupName + '/' + headerName : headerName;
}

/**
 * 年月を年月日に変換する
 * @param {object} data データ
 * @param {string} key キー
 */
export function convertMonthToDate(data, key) {
  if (Object.hasOwn(data, key) && data[key]) {
    data[key] = data[key] + '-01';
  }
}

/**
   * 基準電柱の緯度経度から到着点の緯度経度を計算する
   * @param {number} idBase 基準電柱の緯度
   * @param {number} keidoBase 基準電柱の経度
   * @param {number} hoikaku 方位
   * @param {number} kyori 距離
   * @return {object} 到着点の緯度経度
   */
export function calculateArrivalPoint(idBase, keidoBase, hoikaku, kyori) {
  if (!idBase || !keidoBase || !hoikaku || !kyori) {
    return {ido: null, keido: null};
  }
  const EARTH_RADIUS = 6378137;

  // 到着点の緯度計算
  const earthCircle = EARTH_RADIUS * Math.PI * 2;
  const radius = hoikaku / 180 * Math.PI;
  const lenIdo = kyori * Math.sin(radius);
  const idoPerMeter = 360 / earthCircle;
  const arrivalIdo = idBase + lenIdo * idoPerMeter;

  // 到着点の経度計算
  const lenKeido = kyori * Math.cos(radius);
  const radiusKeido = EARTH_RADIUS * Math.cos(arrivalIdo / 180 * Math.PI);
  const earthCircleKeido = 2 * Math.PI * radiusKeido;
  const keidoPerMeter = 360 / earthCircleKeido;
  const arrivalKeido = keidoBase + lenKeido * keidoPerMeter;

  const retIdo = parseFloat(arrivalIdo.toFixed(6));
  const retKeido = parseFloat(arrivalKeido.toFixed(6));
  return {ido: retIdo, keido: retKeido};
}

/**
 * 文字列の頭文字を大文字にする
 * @param {string} str
 * @return {string} 変換文字列
 */
export function capitalizeFirstLetter(str) {
  if (!str) {
    return str;
  }
  return str.substring(0, 1).toUpperCase() + str.substring(1);
}

/**
 * 行番号を埋込文字列に変換する
 * @param {*} rowIndex
 * @return {string} メッセージに埋め込む行番号
 */
export function formatRowIndex(rowIndex) {
  return rowIndex === '' || rowIndex === ' ' ? rowIndex : '(' + rowIndex + '行目)';
}

/**
   * 選択した電柱の選択入力対象種別をチェックして電柱ID種別を返す
   * @param {object} dnt 電柱選択の戻り値
   * @return {string} 電柱ID種別
   */
export function getDntIdType(dnt) {
  // 電柱マスタ:dnt.infoType == A -> null
  // 共架物件マスタ（設備）: dnt.infoType == B -> 1
  // 共架物件マスタ（分岐点・未登録電柱）:dnt.infoType == B かつ枝番無し -> 2
  // 設備（電柱）: dnt.infoType == D -> 3
  // 分岐点:dnt.infoType == D かつ枝番無し -> 4
  // 未登録電柱:dnt.infoType == C -> 5
  // その他:9 未使用？
  let dntIdType = null;
  if (dnt.infoType === 'B') {
    dntIdType = DNT_ID_TYPE.BUKKEN_SETUBI;
    if (!Object.hasOwn(dnt, 'edaban')) {
      dntIdType = DNT_ID_TYPE.BUKKEN_BUNKI;
    }
  } else if (dnt.infoType === 'C') {
    dntIdType = DNT_ID_TYPE.MITOUROKU;
  } else if (dnt.infoType === 'D') {
    dntIdType = DNT_ID_TYPE.SETUBI;
    if (!Object.hasOwn(dnt, 'edaban')) {
      dntIdType = DNT_ID_TYPE.BUNKI;
    }
  } else {
    // do nothing
  }

  return dntIdType;
}

/**
 * 値が整数かどうか判定する
 * @param {*} value 値
 * @return {boolean} true:整数, false:整数以外
 */
export function isIntValue(value) {
  return /^[+,-]?\d+$/.test(value);
}

/**
 * 値が整数、少数かどうか判定する
 * @param {*} value 値
 * @return {boolean} true:整数または少数, false:整数、少数以外
 */
export function isFloatValue(value) {
  return /^[+,-]?\d+(\.\d+)?$/.test(value);
}
